/**
 * Support tickets.
 *
 * Not one of the §4 product domains, and deliberately narrow: only the ticket
 * list and the compose form live here. Search text and the open-accordion index
 * are route-local, because nothing outside the Support screen reads them.
 *
 * The form fields are in the store rather than local to /ticket for the reason the
 * prototype had them at the top level: the ticket page is reachable from the
 * Support Center, a topic page and the sidebar, and a half-written ticket should
 * survive a look back at the FAQ. Losing it on navigation would be a regression.
 *
 * SIMULATED. No ticket is transmitted and no email is sent — supportApi appends to
 * this list and the toast promises a reply that will not arrive. The list resets on
 * reload. See docs/api-contract.md, "Support tickets".
 */
import { create } from 'zustand';
import { createInitialTickets, supportApi } from '../api/supportApi';
import { USE_MOCK_API } from '../config';
import { TICKET_CATEGORIES } from '../data/faq';
import { apiErrorMessage } from '../api/errors';
import type { SupportTicket } from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';

/** Outcome of a submission, so the caller can toast without a try/catch. */
export type TicketSubmission = { ok: true; ticket: SupportTicket } | { ok: false; error: string };

interface SupportState {
  tickets: SupportTicket[];
  subject: string;
  category: string;
  message: string;
  email: string;
  /**
   * Where the ticket-list READ has got to. See stores/loadState.ts.
   *
   * Distinct from the `TicketSubmission` a `submit()` returns, which is a WRITE outcome
   * handed straight to the caller so it can toast. This is the list's state, and the two
   * cannot share a field: a ticket that failed to send and a ticket list that failed to
   * load are different failures with different remedies.
   */
  load: LoadState;

  setSubject: (subject: string) => void;
  setCategory: (category: string) => void;
  setMessage: (message: string) => void;
  setEmail: (email: string) => void;
  /**
   * Submits the form and prepends the new ticket.
   *
   * Clears the subject and message on success but keeps the category, matching
   * the prototype: the next ticket is usually about the same thing. A rejected
   * submission keeps everything the user typed.
   */
  submit: (authenticated?: boolean) => Promise<TicketSubmission>;
  /**
   * Fetches the ticket list through `supportApi.getTickets()`.
   *
   * The read that had no caller: the store seeded itself with `createInitialTickets()`
   * directly, so — exactly as with the song library — the fixture was the store's initial
   * state rather than the mock's response, and there was no fetch for a backend to
   * replace. `getTickets()` returns the same one seed ticket, so this changes no pixel;
   * what it changes is that the list now arrives through the seam.
   *
   * Nothing calls it yet, for songsStore.loadSongs's reason: the store already holds the
   * same fixture, and a load on mount would also discard any ticket submitted this session
   * — the seed list is all `getTickets()` knows about. §32 rather than a wire-up that
   * looks connected.
   *
   * Never rejects; failures land in `load.error`.
   */
  loadTickets: () => Promise<void>;
  reset: () => void;
}

const INITIAL = {
  subject: '',
  category: TICKET_CATEGORIES[0],
  message: '',
  email: '',
};

function seedTickets(): SupportTicket[] {
  return USE_MOCK_API ? createInitialTickets() : [];
}

export const useSupportStore = create<SupportState>((set, get) => ({
  tickets: seedTickets(),
  load: IDLE_LOAD,
  ...INITIAL,

  setSubject(subject) {
    set({ subject });
  },
  setCategory(category) {
    set({ category });
  },
  setMessage(message) {
    set({ message });
  },
  setEmail(email) {
    set({ email });
  },

  async submit(authenticated = true) {
    const { subject, category, message, email } = get();
    try {
      const ticket = await supportApi.createTicket({
        subject,
        category,
        message,
        authenticated,
        ...(!authenticated ? { email } : null),
      });
      set((s) => ({
        tickets: authenticated ? [ticket, ...s.tickets] : s.tickets,
        subject: '',
        message: '',
        ...(!authenticated ? { email: '' } : null),
      }));
      return { ok: true, ticket };
    } catch (err) {
      /*
        THROUGH THE ONE ERROR CONTRACT, which is what this line used to be an example of
        not doing. It read `err instanceof ApiError ? err.message : 'Could not submit your
        ticket'` — one of the four bespoke error models Batch 7 §21 names ("Do not allow
        every component to invent a different API error model"), and api/errors.ts was
        written to replace. `apiErrorMessage` handles the ApiError case identically and
        additionally covers a failed fetch's TypeError, a thrown string, and this app's own
        capability-refusal classes, whose carefully written messages the old fallback
        discarded in favour of a generic sentence.

        `preserveMessage: false` because this string is rendered to a user. It changes
        nothing for the four cases above — an ApiError keeps its message, a failed fetch
        gets the network sentence, and a capability refusal keeps its own text regardless —
        and it covers the fifth: an unrecognised Error from a genuine bug, whose message
        ("undefined is not a function") is a leak of internals rather than something to
        show someone trying to file a support ticket.
      */
      return { ok: false, error: apiErrorMessage(err, { preserveMessage: false }) };
    }
  },

  async loadTickets() {
    set({ load: LOADING_LOAD });
    try {
      const tickets = await supportApi.getTickets();
      set({ tickets, load: READY_LOAD });
    } catch (err) {
      // The held list stands. See songsStore.loadSongs — a failed refresh is not an empty
      // inbox, and rendering one as the other is how a user concludes their ticket vanished.
      set({ load: failedLoad(err) });
    }
  },

  reset() {
    set({ tickets: seedTickets(), load: IDLE_LOAD, ...INITIAL });
  },
}));
